import React, { forwardRef, ReactElement } from 'react'
import { ViewStyle } from 'react-native'

import { ActionSheetOptions, useActionSheet } from '@expo/react-native-action-sheet'
import { FlashList } from '@shopify/flash-list'
import { isPast } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import {
  canMemberManageSession,
  getDbtTeensParticipantRole,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { ThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'
import { isWithin24Hours } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  getProgramNameFromId,
  isDAEngageProgram,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { getDbtTeensSessionAttendeeNames } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils/getDbtTeensSessionAttendeeNames'
import { isDbtTeensTeenOnlySession } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils/getDbtTeensSessionLabel'
import { CalendarCardAppointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode, RecipientType } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { CostInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { LiveMessagingSessionListIcon } from '../../atoms/icons/LiveMessagingSessionListIcon'
import { LocationIcon } from '../../atoms/icons/LocationIcon'
import { SessionListPhoneIcon } from '../../atoms/icons/SessionListPhoneIcon'
import { VideoIconWithEllipse } from '../../atoms/icons/VideoIconWithEllipse'
import { IS_WEB } from '../../constants'
import { CalendarCard } from '../../molecules/calendarCard/CalendarCard'
import { CalendarCardActionMenu } from '../../molecules/calendarCard/CalendarCardActionMenu'
import { CostEstimateErrorBanner } from '../../molecules/costEstimateErrorBanner/CostEstimateErrorBanner'
import { DbtTeensSessionCard } from '../../molecules/dbtTeensSessionCard/DbtTeensSessionCard'
import { LCTTeensInlineCallout } from '../../molecules/lctTeensInlineCallout/LCTTeensInlineCallout'
import { SessionsCostEstimateDisclaimer } from '../../molecules/sessionsCostEstimateDisclaimer/SessionsCostEstimateDisclaimer'
import { SpacedView } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export enum SESSION_LIST_ITEM_TYPE {
  CARD,
  TITLE,
  EMPTY_MESSAGE,
  COST_ESTIMATE_DISCLAIMER,
  COST_ESTIMATE_ERROR_BANNER,
}

export interface SessionListItem {
  type: SESSION_LIST_ITEM_TYPE
  data: JSX.Element | CalendarCardAppointment
  key: string
  canPress?: boolean
  testId?: string
}

export type SessionListProps = {
  title?: ReactElement
  sessions?: CalendarCardAppointment[]
  /** Combined list in order to optimize on mobile (uses Flashlist). Ignores `sessions` if this is set. */
  combinedSessions?: SessionListItem[]
  onSessionPress?: (
    session: CalendarCardAppointment,
    programName: ProgramNames,
    showActionSheetWithOptions: (options?: ActionSheetOptions, callback?: (i?: number) => void) => void,
    navigateToRescheduleSession?: (appointment: CalendarCardAppointment | number) => void,
    onCancelSessionPress?: (appointment: CalendarCardAppointment) => void,
  ) => void
  addToCalendar?: (appointmentId: { appointmentId: number }) => void
  handleReschedulePress?: (appointment: CalendarCardAppointment) => void
  handleCancelPress?: (appointment: CalendarCardAppointment) => void
  onCancelSessionPress?: (appointment: CalendarCardAppointment) => void
  getProviderIcon?: (meetingFormat: string, isInPast: boolean) => ReactElement
  navigateToRescheduleSession?: (appointment: CalendarCardAppointment) => void
  viewSessionRecording?: (sessionID: string) => void
  emptyListMessage?: ReactElement
  testId?: string
  getCardTitle: (appointment: CalendarCardAppointment) => string
  listFooterComponent?: ReactElement
  listHeaderComponent?: ReactElement
  showManageSessionButtons?: boolean
  style?: ViewStyle
  onPressContactCareNavigator: () => void
  currentUserLyraId?: string
  dbtTeensCaregiverFirstName?: string
  showDbtTeensAttendeeNames?: boolean
  /** Episodes for the sessions in this list, keyed by appointment.episodeId — used to resolve the
   * SECONDARY_PATIENT participant (the DBT Teens caregiver) for canMemberManageSession's primary
   * resolution path. Falls back to guardianLyraId/age heuristics when omitted or not found. */
  episodes?: Episode[]
  isFullHeight?: boolean
  isDAEngageProvider?: boolean
  isSessionsCostEstimateEnabled?: boolean
  shouldShowCostEstimateDisclaimer?: boolean
  handleCostEstimateLinkPress?: (sessionCostInfo: CostInfo) => void
  showCostEstimateErrorBanner?: boolean
  useKilometers?: boolean
}

export const getSessionIcon = (meetingFormat: MEETING_FORMATS, colors: ThemeColors) => {
  switch (meetingFormat) {
    case MEETING_FORMATS.IN_PERSON: {
      return <LocationIcon size={16} isFilled />
    }
    case MEETING_FORMATS.PHONE:
    case MEETING_FORMATS.CALL: {
      return (
        <SessionListPhoneIcon
          backgroundColor={colors.backgroundHighlightPrimary}
          fillColor={colors.iconActive}
          size={24}
        />
      )
    }
    case MEETING_FORMATS.LIVE_MESSAGING: {
      return (
        <LiveMessagingSessionListIcon
          backgroundColor={colors.backgroundHighlightPrimary}
          fillColor={colors.iconActive}
          size={24}
        />
      )
    }
    default: {
      return <VideoIconWithEllipse />
    }
  }
}

const SessionListContainer = styled.View<{ padded: boolean; theme: ThemeType; isFullHeight: boolean }>(
  ({ theme, padded, isFullHeight }) => ({
    ...(isFullHeight && !IS_WEB && { height: '100%' }),
    ...(padded && { marginBottom: theme.breakpoints.isMinWidthDesktop ? theme.spacing['24px'] : 10 }),
    ...(IS_WEB &&
      theme.breakpoints.isMinWidthDesktop && {
        width: '544px',
        margin: '0 auto',
      }),
  }),
)

const SessionListTitle = styled.View({
  fontSize: '18px',
  fontWeight: 'normal',
  lineHeight: '28px',
  marginBottom: 12,
})

/**
 * Handles rendering a group of calendar cards that represent a session with a title
 * renders no sessions if there are no sessions
 */
let SessionList = forwardRef(
  (
    {
      title,
      sessions,
      combinedSessions,
      onSessionPress,
      addToCalendar,
      handleReschedulePress,
      handleCancelPress,
      getProviderIcon,
      onCancelSessionPress,
      navigateToRescheduleSession,
      viewSessionRecording,
      emptyListMessage,
      testId,
      getCardTitle,
      listFooterComponent,
      listHeaderComponent,
      showManageSessionButtons = true,
      style,
      onPressContactCareNavigator,
      currentUserLyraId,
      dbtTeensCaregiverFirstName,
      showDbtTeensAttendeeNames = false,
      episodes,
      isFullHeight = true,
      isDAEngageProvider,
      isSessionsCostEstimateEnabled = false,
      shouldShowCostEstimateDisclaimer = false,
      handleCostEstimateLinkPress,
      showCostEstimateErrorBanner,
      useKilometers = false,
    }: SessionListProps,
    ref: any,
  ) => {
    const { isDAMinorProgramEnabled } = useFlags()
    const { colors } = useTheme()
    const { showActionSheetWithOptions } = useActionSheet()
    const combinedSessionsList = combinedSessions
      ?.filter((item) => item.type === SESSION_LIST_ITEM_TYPE.CARD)
      .map((item) => item.data) as CalendarCardAppointment[]

    const renderListItem = ({ item }: { item: SessionListItem }) => {
      if (item.type === SESSION_LIST_ITEM_TYPE.TITLE) {
        return (<SessionListTitle testID={tID(`SessionList-heading`)}>{item.data}</SessionListTitle>) as JSX.Element
      } else if (
        item.type === SESSION_LIST_ITEM_TYPE.EMPTY_MESSAGE ||
        item.type === SESSION_LIST_ITEM_TYPE.COST_ESTIMATE_DISCLAIMER ||
        item.type === SESSION_LIST_ITEM_TYPE.COST_ESTIMATE_ERROR_BANNER
      ) {
        return item.data as JSX.Element
      }

      const session = item.data as CalendarCardAppointment
      const {
        appointmentId,
        sessionType,
        subtitle,
        meetingFormat,
        avatarDetails,
        canRescheduleAppointment,
        appointmentStatus,
        changeReason,
        programId,
        providerID,
        sessionNumber,
        pastAppointment,
        userId,
        guardianLyraId,
        episodeId,
        childFirstName,
        childLyraId,
        providerFirstName,
        withOnlyBottomBorder,
        costInfo,
        costEstimateDisplayState,
      } = session
      const dateTime = getDateFromAppointment(session)
      const isPastSession = isPast(dateTime)
      const programName = getProgramNameFromId(programId) ?? ProgramNames.DIRECT_ACCESS_THERAPY
      const isDbtTeensProgram = programName === ProgramNames.DialecticalBehaviorTherapyTeens
      const isParentLedLctTeenBooking =
        guardianLyraId != null && userId !== guardianLyraId && currentUserLyraId === guardianLyraId // the appointment is booked for a teen by their guardian
      const isChildBooking =
        isDAMinorProgramEnabled && isDAEngageProgram(programName) ? childFirstName : childFirstName && childLyraId
      const allSessions = combinedSessionsList || sessions
      const onPress =
        onSessionPress && item.canPress && allSessions
          ? () => {
              onSessionPress(
                session,
                programName,
                showActionSheetWithOptions,
                navigateToRescheduleSession
                  ? navigateToRescheduleSession
                  : canRescheduleAppointment
                    ? handleReschedulePress
                    : undefined,
                onCancelSessionPress ? onCancelSessionPress : handleCancelPress,
              )
            }
          : undefined
      const missed =
        appointmentStatus === 'missed' || (appointmentStatus === 'canceled' && changeReason === 'clientInitiated')

      // DBT for Teens (LCDELIVERY-7302 §4.2 + Q-FE-4): the caregiver (parent) may manage all
      // appointment types; the teen may manage all except caregiver ("Parenting") sessions. LCT Teens'
      // guardian-cannot-manage behavior is preserved. See canMemberManageSession for the full rules.
      const episode = episodes?.find((ep) => ep?.id === episodeId)
      const patientId = isDbtTeensProgram ? (episode?.patient_id ?? childLyraId ?? userId) : userId
      const shouldDisallowSessionUpdate = !canMemberManageSession({
        programName,
        sessionType,
        currentUserLyraId,
        patientId,
        guardianLyraId,
        episodeParticipants: episode?.participants,
        appointments: [session],
      })
      const shouldRenderDbtTeensCard = isDbtTeensProgram && currentUserLyraId != null
      const isDbtTeensCaregiver =
        shouldRenderDbtTeensCard &&
        getDbtTeensParticipantRole({
          currentUserLyraId,
          patientId,
          episodeParticipants: episode?.participants,
          appointments: [session],
        }) === RecipientType.CAREGIVER
      const dbtTeensRecipientName =
        isDbtTeensCaregiver && isDbtTeensTeenOnlySession(session) ? childFirstName : undefined
      const dbtTeensAttendeeNames =
        isDbtTeensCaregiver && showDbtTeensAttendeeNames
          ? getDbtTeensSessionAttendeeNames({
              appointment: session,
              teenFirstName: childFirstName,
              caregiverFirstName: dbtTeensCaregiverFirstName,
            })
          : undefined
      const dbtTeensActionMenu =
        shouldRenderDbtTeensCard && !isPastSession && appointmentStatus !== 'canceled' && showManageSessionButtons ? (
          // DBT Teens sessions intentionally omit reschedule callbacks so the menu only shows supported actions.
          <CalendarCardActionMenu
            addToCalendarLink={addToCalendar}
            disallowSessionUpdate={shouldDisallowSessionUpdate}
            handleCancelPress={handleCancelPress}
            id={appointmentId}
            isTooLateToReschedule={isWithin24Hours(session)}
            onCancelSessionPress={onCancelSessionPress}
            session={session}
          />
        ) : undefined

      return (
        <>
          {shouldRenderDbtTeensCard ? (
            <SpacedView marginBottom='8px'>
              <DbtTeensSessionCard
                appointment={session}
                attendeeNames={dbtTeensAttendeeNames}
                recipientName={dbtTeensRecipientName}
                trailingAccessory={dbtTeensActionMenu}
                testID={`DbtTeensSessionCard${item.testId ? `-${item.testId}` : ''}`}
              />
            </SpacedView>
          ) : (
            <CalendarCard
              id={appointmentId}
              testId={item.testId}
              title={getCardTitle(session)}
              subtitle={subtitle}
              icon={getSessionIcon(meetingFormat, colors)}
              avatarDetails={avatarDetails}
              onPress={appointmentStatus !== 'canceled' ? onPress : undefined}
              isMissed={missed}
              ref={ref}
              addToCalendarLink={addToCalendar}
              handleReschedulePress={canRescheduleAppointment || getProviderIcon ? handleReschedulePress : undefined}
              handleCancelPress={handleCancelPress}
              providerIcon={
                getProviderIcon &&
                getProviderIcon(appointmentStatus === 'canceled' ? 'cancelled' : meetingFormat, isPastSession)
              }
              viewSessionRecording={viewSessionRecording}
              session={session}
              showManageSessionButton={showManageSessionButtons}
              providerID={providerID}
              disallowSessionUpdate={shouldDisallowSessionUpdate}
              childFirstName={isParentLedLctTeenBooking || isChildBooking ? childFirstName : undefined}
              isDAEngageProvider={isDAEngageProvider}
              isSessionsCostEstimateEnabled={isSessionsCostEstimateEnabled}
              providerFirstName={providerFirstName}
              withOnlyBottomBorder={withOnlyBottomBorder}
              navigateToRescheduleSession={canRescheduleAppointment ? navigateToRescheduleSession : undefined}
              onCancelSessionPress={onCancelSessionPress}
              costInfo={costInfo}
              costEstimateDisplayState={costEstimateDisplayState}
              handleCostInfoPress={handleCostEstimateLinkPress}
              useKilometers={useKilometers}
            />
          )}
          {providerID &&
            !pastAppointment &&
            programName === ProgramNames.TeensTherapy &&
            sessionNumber === 1 &&
            !isParentLedLctTeenBooking && (
              <>
                <SpacedView marginTop='8px' />
                <LCTTeensInlineCallout
                  onPressContactCareNavigator={onPressContactCareNavigator}
                  isBookingStep={false}
                />
                <SpacedView marginBottom='24px' />
              </>
            )}
        </>
      )
    }

    const getKey = (item: SessionListItem) => item.key

    return (
      <SessionListContainer
        style={style}
        testID={tID(`SessionList-${testId}`)}
        padded={!!sessions}
        isFullHeight={isFullHeight}
      >
        {combinedSessions ? (
          <FlashList
            data={combinedSessions}
            renderItem={renderListItem}
            keyExtractor={getKey}
            getItemType={(item) => item.type}
            maintainVisibleContentPosition={{ disabled: true }}
            contentContainerStyle={{ padding: 16 }}
            ListFooterComponent={listFooterComponent}
            ListHeaderComponent={listHeaderComponent}
          />
        ) : (
          <>
            <SessionListTitle testID={tID(`SessionList-heading`)}>{title}</SessionListTitle>
            {isSessionsCostEstimateEnabled && showCostEstimateErrorBanner && (
              <CostEstimateErrorBanner onPressContactCareNavigator={onPressContactCareNavigator} />
            )}
            {sessions?.length === 0
              ? emptyListMessage
              : sessions?.map((session) => {
                  return renderListItem({
                    item: {
                      data: session,
                      type: SESSION_LIST_ITEM_TYPE.CARD,
                      key: `${session.id}`,
                      canPress: true,
                    },
                  })
                })}
            {isSessionsCostEstimateEnabled && sessions && sessions.length > 0 && shouldShowCostEstimateDisclaimer && (
              <SessionsCostEstimateDisclaimer onPressContactCareNavigator={onPressContactCareNavigator} />
            )}
          </>
        )}
      </SessionListContainer>
    )
  },
)
SessionList = React.memo(SessionList)
export { SessionList }
